"use client";
import type { CreativeTemplatesDemoBlock, Media } from "@mw/shared/payload-types";
import { Block } from "./Block";
import { Image } from "@ui/components/core/Image";
import { BeforeAfterDemo } from "./BlockBentoGrid/BeforeAfterDemo";
import { BaseLink } from "@ui/components/core/Link";
import { formatLink, getVideoUrl } from "@utilities/payload";
import { ImageSizes } from "@ui/lib/image";
import { ArrowUpRight } from "lucide-react";
import { VideoPlayer } from "@ui/components/demos/ImageVideoHero/VideoPlayer";
import { VideoMuteCoordinator } from "@ui/components/demos/ImageVideoHero/VideoMuteCoordinator";
import { useRef } from "react";
import clsx from "clsx";
import { useInView } from "@ui/lib/useInView";
import { ScrollGrid } from "@ui/components/layout/ScrollGrid";

export interface TemplateCardProps {
  template: NonNullable<CreativeTemplatesDemoBlock["templates"]>[number];
}

interface VideoCardProps {
  video: Media | string | null | undefined;
}

function VideoCard({ video }: VideoCardProps) {
  if (!video) return null;
  const videoUrl = getVideoUrl(video);
  if (!videoUrl) return null;

  return (
    <div className='tw-absolute tw-inset-0 tw-w-full tw-h-full'>
      <VideoPlayer autoPlay loop muted src={videoUrl} rounded={false} hasAudio />
    </div>
  );
}

export function TemplateCard({ template }: TemplateCardProps) {
  const { item } = template;
  const link = formatLink(item.link);
  const cardRef = useRef<HTMLDivElement>(null);
  const inView = useInView(cardRef, { once: true, threshold: 0.1 });
  const aspectRatio: string = item.aspectRatio || "portrait";

  return (
    <div
      ref={cardRef}
      className={clsx(
        "tw-group tw-relative tw-w-full tw-overflow-hidden tw-rounded-3xl tw-bg-neutral-100",
        aspectRatio === "square"
          ? "tw-aspect-square"
          : aspectRatio === "portrait-long"
            ? "tw-aspect-[5/7]"
            : "tw-aspect-[5/6]",
      )}
    >
      {/* Media/Demo Section - Full size */}
      <div className='tw-absolute tw-inset-0 tw-w-full tw-h-full'>
        {/* Loading placeholder - shows until content is ready */}
        <div className='tw-absolute tw-inset-0 tw-w-full tw-h-full tw-bg-cream' />

        {inView && (
          <>
            {item.template === "image" && item.image?.image && (
              <Image
                className='tw-w-full tw-h-full tw-object-cover'
                resource={item.image.image}
                sizes={ImageSizes.FULL_1_3_ON_LG}
              />
            )}

            {item.template === "video" && item.video && <VideoCard video={item.video.video} />}

            {item.template === "beforeAfter" && item.beforeAfter && (
              <BeforeAfterDemo
                afterImage={item.beforeAfter.afterImage}
                beforeImage={item.beforeAfter.beforeImage}
                afterVideo={item.beforeAfter.afterVideo}
                beforeVideo={item.beforeAfter.beforeVideo}
                afterLabel={item.beforeAfter.afterLabel}
                beforeLabel={item.beforeAfter.beforeLabel}
              />
            )}
          </>
        )}
      </div>

      <div className='tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-1/3 tw-z-20 tw-bg-gradient-to-t tw-from-black/40 tw-to-transparent' />

      {/* Text Overlay - Position accounting for description height */}
      <div className='tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-z-30 tw-px-5'>
        {/* Content container - fixed height for smooth transitions */}
        <div
          className={clsx(
            "tw-text-center",
            item.description ? "tw-transition-transform tw-duration-300 group-hover:-tw-translate-y-6" : "",
          )}
        >
          <div className='tw-w-full'>
            <div className='tw-flex tw-items-center tw-justify-center tw-gap-1 tw-max-w-[90%] tw-mx-auto'>
              {link ? (
                <BaseLink
                  href={link.href}
                  target={link.target}
                  rel={link.rel}
                  aria-label={link.ariaLabel || item.title}
                  className='tw-inline-flex tw-items-center tw-gap-2'
                >
                  <div className='tw-size-5 md:tw-size-6 tw-opacity-0' />
                  <h3 className='tw-relative tw-z-20 tw-text-white hover:tw-underline tw-underline-offset-2 tw-type-2xl'>
                    {item.title}
                  </h3>
                  <ArrowUpRight className='tw-size-5 md:tw-size-6 tw-text-white tw-flex-shrink-0 tw-translate-y-px' />
                  {/* Stretched link - makes bottom text content of card clickable */}
                  <span className='tw-absolute tw-inset-0 tw-z-10' aria-hidden='true' />
                </BaseLink>
              ) : (
                <h3 className='tw-text-white tw-truncate f-description-01'>{item.title}</h3>
              )}
            </div>
          </div>
          {/* Description - hidden by default, fades in on hover with fixed height */}
          <div className='tw-h-[1.5rem] md:tw-h-[3rem] tw-overflow-hidden tw-mt-1'>
            {item.description && (
              <p
                className='tw-text-white tw-opacity-0 tw-transition-opacity tw-duration-300 group-hover:tw-opacity-90 tw-line-clamp-1 md:tw-line-clamp-2 tw-type-sm'
                style={{ lineHeight: "1.5rem" }}
              >
                {item.description}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function BlockCreativeTemplatesDemo({
  templates,
  showScrollingLegend,
  layout = "scroller",
}: CreativeTemplatesDemoBlock) {
  if (!templates || templates.length === 0) {
    return null;
  }

  return (
    <Block name='creative-templates-demo'>
      <VideoMuteCoordinator>
        {layout === "grid" ? (
          <div className='tw-columns-1 md:tw-columns-2 lg:tw-columns-3 tw-gap-4 tw-container'>
            {templates.map((template, index: number) => (
              <div key={index} className='tw-break-inside-avoid tw-mb-4'>
                <TemplateCard template={template} />
              </div>
            ))}
          </div>
        ) : (
          <ScrollGrid
            items={templates}
            renderItem={template => <TemplateCard template={template} />}
            legend={{
              show: !!showScrollingLegend,
              getItemTitle: template => template.item.title,
              textClassName: "tw-type-base",
            }}
          />
        )}
      </VideoMuteCoordinator>
    </Block>
  );
}
